import { useState } from 'react'
import { useApiKeys, useCreateApiKey, useRevokeApiKey } from '../../hooks/useApiKeys'
import { Card } from '../../components/ui/Card'
import { Spinner } from '../../components/ui/Spinner'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import {
  ResponsiveTable,
  type Column,
} from '../../components/ui/ResponsiveTable'
import { ErrorBanner } from '../../components/ErrorBanner'
import { apiErrorMessage } from '../../api/client'
import type { APIKey, APIKeyWithToken, Glow } from '../../api/types'

// ApiKeysTab manages per-glow API keys used by glow-api (and any
// future server-to-server tooling). Plaintext tokens appear ONCE on
// creation; subsequent list reads only show the hash. To "rotate"
// you create a new key, swap it into your tooling, then revoke the
// old one — there's no in-place rotate verb.
export function ApiKeysTab({ glow }: { glow: Glow }) {
  const { data: keys, isLoading, error } = useApiKeys(glow.id)
  const create = useCreateApiKey(glow.id)
  const [revealed, setRevealed] = useState<APIKeyWithToken | null>(null)
  const [createError, setCreateError] = useState<string | null>(null)
  const [revokeTarget, setRevokeTarget] = useState<APIKey | null>(null)

  const onCreate = async () => {
    setCreateError(null)
    try {
      const k = await create.mutateAsync()
      setRevealed(k)
    } catch (e) {
      setCreateError(apiErrorMessage(e, 'Could not create key.'))
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="text-sm font-medium text-cream">API keys</h3>
            <p className="mt-0.5 text-xs text-muted">
              Use a key with the deploy API to push new versions of this
              glow. To rotate: create a new key, swap it into your
              tooling, then revoke the old one.
            </p>
          </div>
          <Button onClick={onCreate} disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create new key'}
          </Button>
        </div>
        {createError && <ErrorBanner className="mt-3">{createError}</ErrorBanner>}
      </Card>

      <Card className="overflow-hidden p-0">
        {isLoading && (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        )}
        {error && (
          <div className="p-4">
            <ErrorBanner>{(error as Error).message}</ErrorBanner>
          </div>
        )}
        {keys && keys.length === 0 && !isLoading && (
          <p className="p-4 text-sm text-muted">
            No API keys yet. Click <span className="font-medium">Create new key</span> above to mint one.
          </p>
        )}
        {keys && keys.length > 0 && (
          <ResponsiveTable
            columns={keyColumns}
            rows={keys}
            rowKey={(k) => k.id}
            actions={(k) => (
              <Button variant="danger" onClick={() => setRevokeTarget(k)}>
                Revoke
              </Button>
            )}
          />
        )}
      </Card>

      {revealed && (
        <RevealOnceDialog
          token={revealed.token}
          keyHash={revealed.id}
          onClose={() => setRevealed(null)}
        />
      )}
      {revokeTarget && (
        <RevokeDialog
          glowID={glow.id}
          target={revokeTarget}
          onClose={() => setRevokeTarget(null)}
        />
      )}
    </div>
  )
}

// keyColumns drives the API-keys table (and its stacked-card form below sm).
// Pure — the cells only reference the module-level shortHash / fmtDate
// helpers; the per-row Revoke control is wired via the `actions` slot in
// ApiKeysTab (it needs setRevokeTarget from component scope).
const keyColumns: Column<APIKey>[] = [
  {
    header: 'Key hash',
    primary: true,
    cell: (k) => <span className="font-mono text-xs">{shortHash(k.id)}</span>,
  },
  {
    header: 'Created',
    cell: (k) => <span className="text-cream/70">{fmtDate(k.created_at)}</span>,
  },
]

function RevealOnceDialog({
  token,
  keyHash,
  onClose,
}: {
  token: string
  keyHash: string
  onClose: () => void
}) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(token)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard rejected (insecure context, permissions). User can
      // still select + copy manually from the readonly input.
    }
  }
  return (
    <Modal onClose={onClose} size="lg" labelledBy="apikey-reveal-title" dismissible={false}>
      <h3 id="apikey-reveal-title" className="text-lg font-semibold">
        Your new API key
      </h3>
      <p className="mt-2 rounded-md border border-glow-500/25 bg-glow-500/10 px-3 py-2 text-sm text-glow-500">
        <strong>You won&apos;t see this token again.</strong> Copy it now and
        store it securely (e.g. in your deploy environment&apos;s secret store).
      </p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <Input type="text" value={token} readOnly className="font-mono text-xs" />
        <Button onClick={copy} variant="secondary">
          {copied ? 'Copied!' : 'Copy'}
        </Button>
      </div>
      <p className="mt-3 text-xs text-muted">
        Key hash:{' '}
        <code className="font-mono">{shortHash(keyHash)}</code>{' '}
        — this is what appears in the list and logs. We don&apos;t store the
        token itself, only its hash, so copy it now: this is the only time
        it&apos;s shown.
      </p>
      <div className="mt-4 flex justify-end">
        <Button onClick={onClose}>I&apos;ve copied it</Button>
      </div>
    </Modal>
  )
}

function RevokeDialog({
  glowID,
  target,
  onClose,
}: {
  glowID: string
  target: APIKey
  onClose: () => void
}) {
  const revoke = useRevokeApiKey(glowID)
  const [error, setError] = useState<string | null>(null)
  const onConfirm = async () => {
    setError(null)
    try {
      await revoke.mutateAsync(target.id)
      onClose()
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not revoke.'))
    }
  }
  return (
    <Modal onClose={onClose} labelledBy="apikey-revoke-title">
      <h3 id="apikey-revoke-title" className="text-lg font-semibold">
        Revoke this key?
      </h3>
      <p className="mt-2 text-sm text-cream/70">
        Deploys signed with{' '}
        <code className="rounded bg-surface-2 px-1 font-mono text-xs">
          {shortHash(target.id)}
        </code>{' '}
        will fail immediately. This can&apos;t be undone — you&apos;ll have to
        create a new key.
      </p>
      {error && <ErrorBanner className="mt-3">{error}</ErrorBanner>}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={revoke.isPending}>
          Cancel
        </Button>
        <Button variant="danger" onClick={onConfirm} disabled={revoke.isPending}>
          {revoke.isPending ? 'Revoking…' : 'Revoke'}
        </Button>
      </div>
    </Modal>
  )
}

function shortHash(s: string): string {
  return s.length > 16 ? `${s.slice(0, 12)}…${s.slice(-4)}` : s
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}
